import type { ConfigSecretPath } from '@/api/types.gen';

export interface ConfigTabDefinition {
  id: string;
  label: string;
  fieldKeys: readonly string[];
  secretPaths: readonly ConfigSecretPath[];
}

export interface ConfigSectionDefinition {
  key: string;
  label: string;
  /** A compact section still owns one General tab, but does not render tab chrome. */
  compact?: boolean;
  tabs: readonly ConfigTabDefinition[];
}

export interface ConfigGroupDefinition {
  id: string;
  label: string;
  sections: readonly ConfigSectionDefinition[];
}

const tab = (
  id: string,
  label: string,
  fieldKeys: readonly string[],
  secretPaths: readonly ConfigSecretPath[] = [],
): ConfigTabDefinition => ({ id, label, fieldKeys, secretPaths });

/**
 * The one ownership registry for the Config editor.
 *
 * Every ordinary top-level field and every managed secret belongs to exactly
 * one tab. Source-of-truth guard tests compare this registry with the generated
 * Rust bindings, so adding a setting without giving it a visible home fails the
 * frontend suite instead of silently making it unreachable.
 */
export const CONFIG_NAVIGATION = [
  {
    id: 'integrations',
    label: 'Integrations',
    sections: [
      {
        key: 'encorepro_postgres',
        label: 'EncorePro',
        tabs: [
          tab('connection', 'Connection & tenant', ['host', 'port', 'database', 'user', 'sslmode', 'schema', 'tenant_id']),
          tab('capacity', 'Capacity & retry', ['pool_size', 'rate_limit_rps', 'connect_retry_interval_secs', 'connect_timeout_secs', 'connect_max_attempts']),
          tab('credentials', 'Credentials', [], ['encorepro_postgres.password']),
        ],
      },
      {
        key: 'meta',
        label: 'Meta',
        tabs: [
          tab('account', 'Account & API', ['ad_account_id', 'business_id', 'app_id', 'graph_api_base']),
          tab('identity', 'Identity & measurement', ['page_id', 'instagram_user_id', 'purchase_pixel_id']),
          tab('credentials', 'Credentials', [], ['meta.access_token', 'meta.app_secret']),
        ],
      },
      {
        key: 'ttd',
        label: 'TTD',
        tabs: [
          tab('account', 'Account & API', ['base', 'advertiser_id', 'region']),
          tab('attribution', 'Attribution tags', ['tracking_tag_id', 'secondary_tracking_tag_id']),
          tab('targeting', 'Targeting', ['seed_id', 'geo_segment_id']),
          tab('bidding', 'TTD bidding', [
            'roi_goal_vcpm_cents',
            'private_contract_bid_list_id',
            'ctv_base_bid_cpm_cents',
            'ctv_max_bid_cpm_cents',
            'display_base_bid_cpm_cents',
            'display_max_bid_cpm_cents',
          ]),
          tab('credentials', 'Credentials', [], ['ttd.bearer', 'ttd.crm_bearer']),
        ],
      },
      {
        key: 'enrich',
        label: 'Data Enrichment',
        tabs: [
          tab('general', 'General', ['base', 'max_batch', 'max_concurrent_requests']),
          tab('credentials', 'Credentials', [], ['enrich.api_key']),
        ],
      },
      {
        key: 'r2',
        label: 'File Storage',
        tabs: [
          tab('storage', 'Storage', ['account_id', 'bucket', 'endpoint']),
          tab('credentials', 'Credentials', [], ['r2.s3_access_key_id', 'r2.s3_secret']),
        ],
      },
      {
        key: 'email',
        label: 'Email',
        tabs: [
          tab('sender', 'Sender & API', ['api_base', 'account_id', 'from', 'analytics_zone_id']),
          tab('credentials', 'Credentials', [], ['email.api_token']),
        ],
      },
      {
        key: 'invoicing',
        label: 'Invoicing',
        tabs: [
          tab('run', 'Billing run', ['enabled', 'run_weekday', 'auto_approve', 'reference_prefix', 'first_period', 'catch_up_weeks', 'email_subject_template']),
          tab('adjustments', 'Adjustments', ['adjustments_enabled', 'adjustment_min_cents', 'adjustment_window_weeks', 'adjustment_email_subject_template']),
          tab('xero', 'Xero', ['xero_client_id', 'xero_contact_id', 'xero_item_code', 'xero_fee_item_code', 'management_fee_bps', 'xero_invoice_number_prefix', 'xero_api_base', 'xero_identity_base', 'xero_account_code', 'xero_tax_type']),
          tab('recipients', 'Recipients', ['recipients_to', 'recipients_cc']),
          tab('credentials', 'Credentials', [], ['invoicing.xero_client_secret']),
        ],
      },
      {
        key: 'llm',
        label: 'AI/LLM',
        tabs: [
          tab('brief', 'Daily Briefing', ['brief_model', 'brief_effort', 'brief_max_tokens', 'brief_timeout_secs']),
          tab('explain', 'Explain', ['explain_model', 'explain_effort', 'explain_max_tokens', 'explain_timeout_secs']),
          tab('connection', 'Connection', ['max_concurrent', 'cache_ttl', 'refusal_fallback']),
          tab('credentials', 'Credentials', [], ['llm.api_key']),
        ],
      },
    ],
  },
  {
    id: 'pipeline',
    label: 'Pipeline',
    sections: [
      {
        key: 'pixel_ingest',
        label: 'Pixel Ingest',
        tabs: [
          tab('identity', 'Identity', ['resolve_identity']),
          tab('buffering', 'Buffering', ['flush_max_rows', 'flush_max_bytes', 'flush_interval_secs', 'channel_capacity']),
        ],
      },
      {
        key: 'pixel_kv',
        label: 'Pixel Collector Sync',
        tabs: [
          tab('projection', 'Projection', ['enabled', 'api_base', 'account_id', 'namespace_id']),
          tab('credentials', 'Credentials', [], ['pixel_kv.api_token']),
        ],
      },
      {
        key: 'flow_tap',
        label: 'Flow Tap',
        compact: true,
        tabs: [tab('general', 'General', ['enabled', 'capacity', 'max_age_secs'])],
      },
      {
        key: 'conversions',
        label: 'Conversions',
        tabs: [
          tab('general', 'General', ['enabled', 'mode']),
          tab('meta', 'Meta', ['meta']),
          tab('ttd', 'TTD', ['ttd']),
          tab('uid2', 'UID2', ['uid2']),
          tab('delivery', 'Delivery', ['delivery']),
          tab('tickets', 'Tickets', ['tickets']),
          tab('credentials', 'Credentials', [], [
            'conversions.meta.access_token',
            'conversions.ttd.auth_secret',
            'conversions.uid2.api_key',
            'conversions.uid2.client_secret_b64',
          ]),
        ],
      },
      {
        key: 'delivery_reporting',
        label: 'Delivery Reporting',
        tabs: [
          tab('windows', 'Windows & settlement', ['enabled', 'lookback_days', 'settled_lag_days', 'delivery_settled_lag_days']),
          tab('capacity', 'Capacity', ['max_mappings_per_run', 'max_pages_per_mapping']),
          tab('meta', 'Meta', ['meta_page_size']),
          tab('ttd', 'TTD', ['ttd_creative_page_size', 'ttd_report_timeout_minutes', 'ttd_delivery_template_id', 'ttd_attribution_template_id', 'ttd_intraday_mode']),
        ],
      },
      {
        key: 'copilot',
        label: 'Copilot',
        tabs: [
          tab('cache', 'Cache & runtime', ['enabled', 'cache_retention_days', 'cache_fresh_secs']),
          tab('brief', 'Daily briefing', ['brief_enabled', 'brief_notify']),
        ],
      },
      {
        key: 'log_archive',
        label: 'Log Archive',
        compact: true,
        tabs: [tab('general', 'General', ['enabled', 'prefix', 'retention_days'])],
      },
      {
        key: 'alerts',
        label: 'Alerts',
        tabs: [
          tab('notifications', 'Notifications', ['enabled', 'email_to', 'console_url']),
          tab('detection', 'Detection', ['spike_threshold', 'scan_window_minutes', 'suppression_hours']),
          tab('credentials', 'Credentials', [], ['alerts.webhook_url']),
        ],
      },
    ],
  },
  {
    id: 'scheduling',
    label: 'Scheduling',
    sections: [
      {
        key: 'scheduler',
        label: 'Scheduler',
        tabs: [
          tab('ingest', 'Ingest & backfill', ['encorepro_backfill_cron', 'backfill_health_cron', 'sync_status_sweeper_cron']),
          tab('reporting', 'Reporting', ['delivery_reporting_cron', 'ttd_intraday_delivery_cron']),
          tab('conversions', 'Conversions', ['conversion_delivery_cron', 'ticket_conversions_cron', 'conversions_gc_cron', 'uid2_refresh_cron', 'uid2_prospect_refresh_cron']),
          tab('audiences', 'Audiences & creative', ['audience_materializer_cron', 'lookalike_model_refresh_cron', 'audience_benchmark_refresh_cron', 'creative_upload_gc_cron']),
          tab('monitoring', 'Monitoring', ['pixel_health_cron', 'alert_scan_cron', 'heartbeat_cron']),
          tab('retention', 'Briefing & retention', ['copilot_brief_cron', 'copilot_gc_cron', 'audit_gc_cron', 'creative_review_gc_cron']),
          tab('invoicing', 'Invoicing', ['invoicing_scan_cron', 'invoicing_actions_cron', 'invoicing_payment_sync_cron']),
          tab('retired', 'Retired (no effect)', ['attribution_cron', 'rollup_builder_cron']),
        ],
      },
    ],
  },
  {
    id: 'tools',
    label: 'Tools',
    sections: [
      {
        key: 'log_triage',
        label: 'Log Triage',
        tabs: [
          tab('scope', 'Scope & output', ['lookback_hours', 'output_dir', 'environments']),
          tab('limits', 'Limits', ['max_lines_per_environment', 'max_groups', 'samples_per_group', 'trace_lines_per_group']),
        ],
      },
    ],
  },
  {
    id: 'edge',
    label: 'Platform Controls',
    sections: [
      {
        key: 'edge',
        label: 'Platform Controls',
        tabs: [
          tab('publishing', 'Platform writes', ['campaign_platform_writes_enabled', 'creative_platform_writes_enabled', 'meta_custom_conversion_writes_enabled', 'campaign_extend_meta_enabled', 'campaign_extend_ttd_enabled']),
          tab('audiences', 'Audience & privacy', ['audience_platform_writes_enabled', 'prospect_audience_uploads_enabled', 'audience_household_expansion_enabled', 'lookalike_recent_buyers_enabled', 'lookalike_recent_buyer_days', 'lookalike_recent_buyers_embed_audience_ids']),
          tab('operations', 'Reconciliation & liveness', ['provider_drift_remediation_enabled', 'alert_email_to']),
          tab('claude', 'Connectors & API access', [
            'api_keys_enabled',
            'mcp_enabled',
            'credential_prepare_enabled',
            'credential_admin_enabled',
          ]),
        ],
      },
    ],
  },
] as const satisfies readonly ConfigGroupDefinition[];

export type AdminConfigSection = typeof CONFIG_NAVIGATION[number]['sections'][number]['key'];

export interface AdminConfigSearch {
  section: AdminConfigSection;
  tab: string;
}

export const ADMIN_CONFIG_DEFAULT_SEARCH: AdminConfigSearch = {
  section: 'meta',
  tab: 'account',
};

export const CONFIG_SECTIONS: readonly ConfigSectionDefinition[] = CONFIG_NAVIGATION.flatMap(
  (group) => [...group.sections] as ConfigSectionDefinition[],
);

export function getConfigSection(section: string): ConfigSectionDefinition | undefined {
  return CONFIG_SECTIONS.find((candidate) => candidate.key === section);
}

export function getConfigTab(
  section: ConfigSectionDefinition,
  tabId: string,
): ConfigTabDefinition | undefined {
  return section.tabs.find((candidate) => candidate.id === tabId);
}

export function parseAdminConfigSearch(input: Record<string, unknown>): AdminConfigSearch {
  const requestedSection = typeof input.section === 'string' ? input.section : '';
  const section = getConfigSection(requestedSection);
  const requestedTab = typeof input.tab === 'string' ? input.tab : '';
  if (!section) return ADMIN_CONFIG_DEFAULT_SEARCH;
  // A known section with a missing or stale tab lands on that section's first
  // tab. `?section=ttd` is the URL an operator types, and a renamed tab id must
  // degrade a bookmark to its section, never teleport it to Meta.
  const activeTab = getConfigTab(section, requestedTab) ?? section.tabs[0]!;
  return { section: section.key as AdminConfigSection, tab: activeTab.id };
}

export function updateAdminConfigSearch(
  current: AdminConfigSearch,
  patch: Partial<AdminConfigSearch>,
): AdminConfigSearch {
  const nextSection = patch.section ?? current.section;
  const sectionChanged = nextSection !== current.section;
  const section = getConfigSection(nextSection);
  if (sectionChanged && section && patch.tab === undefined) {
    return { section: section.key as AdminConfigSection, tab: section.tabs[0]!.id };
  }
  return parseAdminConfigSearch({
    section: nextSection,
    tab: patch.tab ?? current.tab,
  });
}

export function serializeAdminConfigSearch(search: AdminConfigSearch): AdminConfigSearch {
  return { section: search.section, tab: search.tab };
}

export function tabForConfigPath(
  section: ConfigSectionDefinition,
  path: string,
): ConfigTabDefinition | undefined {
  const secretOwner = section.tabs.find((candidate) => candidate.secretPaths.includes(path as ConfigSecretPath));
  if (secretOwner) return secretOwner;
  const prefix = `${section.key}.`;
  const relative = path.startsWith(prefix) ? path.slice(prefix.length) : path;
  const topLevelKey = relative.split('.')[0] ?? '';
  return section.tabs.find((candidate) => candidate.fieldKeys.includes(topLevelKey));
}
